import { useMemo, useState, useEffect } from 'react';
import { FCX } from '@datassential/platform-ui-lib';
import { useLandscapeView } from '@pages/Home/context/LandscapeViewContext';
import HomeSectionTabs from '@pages/Home/components/HomeSectionTabs';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import HomeSectionLoader from '@pages/Home/components/HomeSectionLoader';
import { useTerritorySelection } from '@pages/Home/context/TerritorySelectionContext';
import { useLandscapeRestaurantData } from '@apiHooks/useLandscapeRestaurantData';
import { useLandscapePublications, LANDSCAPE_PUBLICATION_TAGS, ON_SITE_PUBLICATION_TAGS } from '@apiHooks/useLandscapePublications';
import TerritoryScopeLabel from '@pages/Home/components/TerritoryScopeLabel/TerritoryScopeLabel';
import { computeTotalRow } from './HomeLandscape.helpers';
import { LandscapeTabEnum } from './HomeLandscape.constants';
import LandscapeCuisinesChart from './LandscapeCuisinesChart';
import LandscapeSegmentsTable from './LandscapeSegmentsTable';
import LandscapeOnSiteTable from './LandscapeOnSiteTable';
import LandscapeReadAboutIt from './LandscapeReadAboutIt';
import HomeSectionDelimiter from '@pages/Home/components/HomeSectionDelimiter';
import RestaurantOpeningsClosings from '../RestaurantOpeningsClosings/RestaurantOpeningsClosings';
import './HomeLandscape.scss';

const LANDSCAPE_TABS = [
    { id: LandscapeTabEnum.RESTAURANT, title: 'Restaurants' },
    { id: LandscapeTabEnum.ON_SITE, title: 'On-Site' },
];


const HomeLandscape: FCX = () => {
    const { savedSelection } = useTerritorySelection();
    const { landscapeView: activeTab, setLandscapeView: setActiveTab } = useLandscapeView();
    const { data, isLoading: isDataLoading } = useLandscapeRestaurantData(savedSelection);
    const [restaurantPubsEnabled, setRestaurantPubsEnabled] = useState(
        () => activeTab === LandscapeTabEnum.RESTAURANT,
    );
    const [onSitePubsEnabled, setOnSitePubsEnabled] = useState(
        () => activeTab === LandscapeTabEnum.ON_SITE,
    );
    useEffect(() => {
        if (activeTab === LandscapeTabEnum.RESTAURANT) setRestaurantPubsEnabled(true);
        if (activeTab === LandscapeTabEnum.ON_SITE) setOnSitePubsEnabled(true);
    }, [activeTab]);

    const { data: restaurantPublications, isLoading: isRestaurantPubsLoading } =
        useLandscapePublications(restaurantPubsEnabled ? { tags: LANDSCAPE_PUBLICATION_TAGS } : null);
    const { data: onSitePublications, isLoading: isOnSitePubsLoading } =
        useLandscapePublications(onSitePubsEnabled ? { tags: ON_SITE_PUBLICATION_TAGS } : null);

    const publications =
        activeTab === LandscapeTabEnum.ON_SITE ? onSitePublications : restaurantPublications;
    const isPublicationsLoading =
        activeTab === LandscapeTabEnum.ON_SITE ? isOnSitePubsLoading : isRestaurantPubsLoading;
    const isLoading = isDataLoading || isPublicationsLoading;

    const sortedCuisines = useMemo(
        () => [...data.cuisines].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
        [data.cuisines],
    );

    const totalRow = useMemo(() => computeTotalRow(data.segments), [data.segments]);

    return (
        <>
            <div className="Home__wrapper">
                <HomeSectionHeader>
                    <div className="HomeLandscape__header">
                        <HomeSectionTitle testId="textHomeLandscapeTitle">
                            Landscape: <TerritoryScopeLabel />
                        </HomeSectionTitle>
                        <HomeSectionTabs
                            data={LANDSCAPE_TABS}
                            activeTabId={activeTab}
                            onItemClick={(id) => setActiveTab(id as LandscapeTabEnum)}
                            testId="blockHomeLandscapeTabs"
                        />
                    </div>
                </HomeSectionHeader>

                <div className="Home__sub-wrapper HomeLandscape__wrapper">
                    {isLoading ? (
                        <HomeSectionLoader />
                    ) : (
                        <div className="HomeLandscape__body">
                            <div className="HomeLandscape__main">
                                {activeTab === LandscapeTabEnum.ON_SITE ? (
                                <LandscapeOnSiteTable data={data.onSiteSegments} />
                            ) : (
                                <><LandscapeCuisinesChart cuisines={sortedCuisines} />
                                <LandscapeSegmentsTable rows={data.segments} totalRow={totalRow}
                            />
                                </>
                            )}</div>
                            {publications.length > 0 && (<div className="HomeLandscape__aside">
                                <LandscapeReadAboutIt publications={publications} />
                            </div>)}
                        </div>
                    )}
                </div>
            </div>
            <RestaurantOpeningsClosings />
            <div className="Home__wrapper HomeLandscape__delimiter-wrapper">
                <HomeSectionDelimiter colors={['#1DFEEC', '#4FB0F9', '#FF59B9', '#FF0395']} />
            </div>
        </>
    );
};

export default HomeLandscape;
